/*
 * Copyright (C) Contributors to the Suwayomi project
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 */

import type {
    YomiriChapter,
    YomiriExtension,
    YomiriManga,
    YomiriMangasPage,
    YomiriPage,
} from '@/yomiri/types/extension.ts';
import { extensionBridgeClient } from '@/yomiri/bridge/ExtensionBridgeClient.ts';

const MAL_API = 'https://api.jikan.moe/v4';

/**
 * Extension-Bridged Real Source (MyAnimeList / Jikan Manga Catalog)
 * 
 * Demonstrates and exercises the Manifest V3 Companion Extension bridge.
 * Requires explicit user host permission for "https://api.jikan.moe/*".
 * If permission is missing or revoked, the extension bridge refuses the request
 * and yields a clear diagnostic error for the user.
 */
export class BridgedTestMangaSource implements YomiriExtension {
    public readonly id = 'bridged-mal';

    public readonly name = 'MyAnimeList Catalog (Bridge Required)';

    public readonly lang = 'en';

    public readonly version = '1.0.0';

    public readonly supportsLatest = true;

    public readonly requiresExtension = true;

    public readonly requiredDomains = ['https://api.jikan.moe/*'];

    public readonly iconUrl = `data:image/svg+xml;utf8,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
      <circle cx="50" cy="50" r="46" fill="#2e51a2"/>
      <text x="50" y="60" font-family="Arial, sans-serif" font-size="28" font-weight="bold" fill="#ffffff" text-anchor="middle">MAL</text>
    </svg>`)}`;

    public async checkHealth(): Promise<{ ok: boolean; message: string }> {
        try {
            const hasPerm = await extensionBridgeClient.checkPermission('https://api.jikan.moe/*');
            if (!hasPerm) {
                return {
                    ok: false,
                    message: 'Permiso de host pendiente: concedé acceso a https://api.jikan.moe/* en la extensión.',
                };
            }
            const data = await extensionBridgeClient.fetchJson(`${MAL_API}/top/manga?limit=1`);
            if (data?.data) {
                return { ok: true, message: 'Fuente conectada exitosamente a través del puente.' };
            }
            return { ok: false, message: 'Respuesta inesperada de la API.' };
        } catch (err: any) {
            return { ok: false, message: err.message || 'Error al conectar con la fuente.' };
        }
    }

    public async getPopular(page: number = 1): Promise<YomiriMangasPage> {
        const url = `${MAL_API}/top/manga?page=${page}&limit=20`;
        const json = await extensionBridgeClient.fetchJson(url);
        const list = json.data || [];

        return {
            mangas: list.map((item: any) => this.formatManga(item)),
            hasNextPage: json.pagination?.has_next_page || false,
        };
    }

    public async getLatest(page: number = 1): Promise<YomiriMangasPage> {
        const url = `${MAL_API}/manga?order_by=start_date&sort=desc&page=${page}&limit=20`;
        const json = await extensionBridgeClient.fetchJson(url);
        const list = json.data || [];

        return {
            mangas: list.map((item: any) => this.formatManga(item)),
            hasNextPage: json.pagination?.has_next_page || false,
        };
    }

    public async search(query: string, page: number = 1): Promise<YomiriMangasPage> {
        const clean = query.trim();
        if (!clean) {
            return this.getPopular(page);
        }

        const url = `${MAL_API}/manga?q=${encodeURIComponent(clean)}&page=${page}&limit=20`;
        const json = await extensionBridgeClient.fetchJson(url);
        const list = json.data || [];

        return {
            mangas: list.map((item: any) => this.formatManga(item)),
            hasNextPage: json.pagination?.has_next_page || false,
        };
    }

    public async getManga(mangaId: string): Promise<YomiriManga> {
        const url = `${MAL_API}/manga/${mangaId}`;
        const json = await extensionBridgeClient.fetchJson(url);
        return this.formatManga(json.data);
    }

    public async getChapters(mangaId: string): Promise<YomiriChapter[]> {
        const _manga = await this.getManga(mangaId);
        // Build representative chapter sequence
        const chapters: YomiriChapter[] = [];
        const count = 12;

        for (let i = 1; i <= count; i++) {
            chapters.push({
                id: `${mangaId}_ch_${i}`,
                mangaId,
                sourceId: this.id,
                name: `Chapter ${i}`,
                chapterNumber: i,
                uploadDate: new Date(Date.now() - (count - i) * 86400000 * 7).toISOString(),
                scanlator: 'Bridge Reader',
                pageCount: 5,
                isRead: false,
                isBookmarked: false,
                lastPageRead: 0,
            });
        }

        return chapters.reverse();
    }

    public async getPages(chapterId: string): Promise<YomiriPage[]> {
        // High quality preview pages for the chapter
        const pages: YomiriPage[] = [];
        for (let i = 0; i < 5; i++) {
            pages.push({
                index: i,
                imageUrl: `data:image/svg+xml;utf8,${encodeURIComponent(`
                    <svg xmlns="http://www.w3.org/2000/svg" width="900" height="1350" viewBox="0 0 900 1350">
                        <rect width="900" height="1350" fill="#18181b"/>
                        <rect x="30" y="30" width="840" height="1290" fill="#27272a" rx="12"/>
                        <circle cx="450" cy="400" r="100" fill="#2e51a2" opacity="0.3"/>
                        <text x="450" y="415" font-family="sans-serif" font-size="42" font-weight="bold" fill="#ffffff" text-anchor="middle">MAL BRIDGE SOURCE</text>
                        <text x="450" y="520" font-family="sans-serif" font-size="28" fill="#a1a1aa" text-anchor="middle">Capítulo: ${chapterId}</text>
                        <text x="450" y="580" font-family="sans-serif" font-size="36" font-weight="bold" fill="#38bdf8" text-anchor="middle">Página ${i + 1} de 5</text>
                        <text x="450" y="700" font-family="sans-serif" font-size="22" fill="#71717a" text-anchor="middle">Cargado mediante Yomiri Companion Bridge (MV3)</text>
                        <line x1="150" y1="760" x2="750" y2="760" stroke="#3f3f46" stroke-width="2"/>
                    </svg>
                `)}`,
            });
        }
        return pages;
    }

    private formatManga(item: any): YomiriManga {
        const genres = (item.genres || []).map((g: any) => g.name);
        const authors = (item.authors || []).map((a: any) => a.name).join(', ');

        let status: YomiriManga['status'] = 'UNKNOWN';
        if (item.status === 'Publishing') status = 'ONGOING';
        else if (item.status === 'Finished') status = 'COMPLETED';
        else if (item.status === 'On Hiatus') status = 'ON_HIATUS';
        else if (item.status === 'Discontinued') status = 'CANCELLED';

        return {
            id: String(item.mal_id),
            sourceId: this.id,
            title: item.title_english || item.title || 'Título Desconocido',
            description: item.synopsis || 'Sin descripción disponible.',
            coverUrl: item.images?.webp?.large_image_url || item.images?.jpg?.large_image_url || '',
            author: authors || 'Autor Desconocido',
            artist: authors || 'Artista Desconocido',
            status,
            genres,
            url: item.url,
            inLibrary: false,
        };
    }
}

export const bridgedTestMangaSourceInstance = new BridgedTestMangaSource();
